<template>
  <div class="whatsapp-container">
    <div class="whatsapp-panels">
      <!-- Sidebar: canales -->
      <aside
        class="sidebar"
        v-show="!isMobile || !selectedChannel"
      >
        <div class="sidebar-top">
          <v-toolbar flat color="#f0f2f5" dense class="sidebar-header">
            <v-list-item-avatar color="primary" class="mr-3" size="36">
              <v-icon dark small>mdi-account</v-icon>
            </v-list-item-avatar>
            <v-toolbar-title class="font-weight-medium sidebar-title">Chats</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="fetchChannels" :loading="loadingChannels">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Actualizar Chats</span>
            </v-tooltip>
          </v-toolbar>
          <div class="sidebar-search pa-2">
            <v-text-field
              v-model="searchChannel"
              placeholder="Busca un chat..."
              outlined
              dense
              hide-details
              rounded
              prepend-inner-icon="mdi-magnify"
              class="search-input"
              background-color="#f0f2f5"
            ></v-text-field>
          </div>
          <v-divider></v-divider>
        </div>
        <div class="channel-list-scroll">
          <v-list class="channel-list pa-0" v-if="filteredChannels.length > 0">
            <v-list-item
              v-for="channel in filteredChannels"
              :key="channel.id"
              @click="selectChannel(channel)"
              :class="{ 'active-channel': selectedChannel && selectedChannel.id === channel.id }"
              class="channel-item"
            >
              <v-list-item-avatar :color="getChannelColor(channel.name)">
                <span class="white--text font-weight-bold">{{ channel.name.charAt(0).toUpperCase() }}</span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium d-flex justify-space-between align-center">
                  <span class="channel-name-text">{{ channel.name }}</span>
                  <span class="caption grey--text flex-shrink-0 ml-2" v-if="channel.updatedAt">{{ formatDate(channel.updatedAt) }}</span>
                </v-list-item-title>
                <v-list-item-subtitle class="text-truncate d-flex align-center">
                  <v-icon small color="grey" class="mr-1" v-if="channel.isClosed">mdi-lock</v-icon>
                  <span v-if="channel.membershipStatus === 'pending'" class="warning--text font-weight-bold mr-1">
                    [Invitación]
                  </span>
                  {{ channel.description || 'Sin descripción' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else class="text-center pa-5 grey--text">
            No tienes chtas asignados.
          </div>
        </div>
      </aside>

      <!-- Chat: mensajes -->
      <section
        class="chat-area"
        v-show="!isMobile || selectedChannel"
      >
        <template v-if="selectedChannel">
          <div class="chat-top">
            <v-toolbar flat color="#f0f2f5" dense class="chat-header">
              <v-btn icon class="d-md-none mr-1" @click="closeChannelMobile">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-list-item-avatar :color="getChannelColor(selectedChannel.name)" size="40">
                <span class="white--text font-weight-bold">{{ selectedChannel.name.charAt(0).toUpperCase() }}</span>
              </v-list-item-avatar>
              <v-toolbar-title class="font-weight-medium chat-header-title">
                {{ selectedChannel.name }}
                <div class="caption grey--text text-truncate chat-header-desc">
                  {{ selectedChannel.description }}
                </div>
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-chip v-if="selectedChannel.isClosed" small color="grey lighten-2" class="mr-2">
                <v-icon small left>mdi-lock</v-icon> Solo lectura
              </v-chip>
            </v-toolbar>
          </div>

          <!-- Pending Invitation State -->
          <div
            v-if="selectedChannel.membershipStatus === 'pending'"
            class="channel-status-panel d-flex flex-column align-center justify-center pa-5 text-center"
          >
            <v-icon size="80" color="warning" class="mb-4">mdi-email-open-outline</v-icon>
            <h2 class="font-weight-regular mb-2 status-title">Has sido invitado a este chat</h2>
            <p class="grey--text mb-6 px-2">Para ver los mensajes y participar, debes aceptar la invitación.</p>
            <div class="d-flex flex-wrap justify-center">
              <v-btn color="error" outlined class="ma-2" @click="rejectInvitation(selectedChannel)" :loading="actionLoading">
                Rechazar
              </v-btn>
              <v-btn color="primary" class="ma-2" @click="acceptInvitation(selectedChannel)" :loading="actionLoading">
                Aceptar Invitación
              </v-btn>
            </div>
          </div>

          <!-- Accepted State: Messages -->
          <template v-else-if="selectedChannel.membershipStatus === 'accepted'">
            <div
              class="chat-messages"
              ref="messagesContainer"
              @scroll="onMessagesScroll"
            >
              <div v-if="loadingMessages" class="text-center my-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
              <template v-else>
                <div v-if="loadingMoreMessages" class="text-center my-2">
                  <v-progress-circular indeterminate color="primary" size="24" width="2"></v-progress-circular>
                  <div class="caption grey--text mt-1">Cargando mensajes anteriores...</div>
                </div>
                <div v-else-if="hasMoreMessages" class="text-center my-2">
                  <span class="caption grey--text bg-white-transparent px-3 py-1 rounded-lg d-inline-block">
                    Desplaza hacia arriba para ver más
                  </span>
                </div>
                <div
                  v-if="messages.length === 0"
                  class="text-center my-4 grey--text bg-white-transparent pa-2 rounded-lg d-inline-block mx-auto align-self-center"
                >
                  No hay mensajes. {{ selectedChannel.isClosed ? 'El administrador aún no ha publicado nada.' : 'Envía el primer mensaje.' }}
                </div>
                <div
                  v-for="group in groupedMessages"
                  :key="group.key"
                  class="message-date-group"
                >
                  <div class="date-separator">
                    <span class="date-separator-label">{{ group.label }}</span>
                  </div>
                  <div
                    v-for="msg in group.messages"
                    :key="msg.id"
                    class="message-wrapper d-flex mb-3"
                    :class="isMyMessage(msg) ? 'justify-end' : 'justify-start'"
                  >
                    <div class="message-outer" :class="isMyMessage(msg) ? 'message-outer--mine' : 'message-outer--other'">

                      <div
                        class="message-card elevation-1 pa-2"
                        :class="getMessageClass(msg)"
                      >
                        <div class="d-flex align-center mb-1 flex-wrap" v-if="!isMyMessage(msg)">
                          <v-avatar size="20" :color="isAdminMessage(msg) ? 'deep-purple lighten-2' : 'teal lighten-2'" class="mr-1 flex-shrink-0">
                            <span style="font-size:10px; color:#fff; font-weight:bold;">
                              {{ getSenderInitial(msg) }}
                            </span>
                          </v-avatar>
                          <div class="sender-info">
                            <span class="caption font-weight-bold" :class="isAdminMessage(msg) ? 'deep-purple--text' : 'teal--text'">
                              {{ getSenderFullName(msg) }}
                            </span>
                            <v-chip
                              v-if="isAdminMessage(msg)"
                              x-small
                              :color="isAdminMessage(msg) ? 'deep-purple lighten-5' : 'teal lighten-5'"
                              :text-color="isAdminMessage(msg) ? 'deep-purple darken-3' : 'teal darken-3'"
                              class="ml-1 sender-address-chip"
                            >
                              <v-icon style="font-size:9px" class="mr-1">{{ isAdminMessage(msg) ? 'mdi-shield-account' : 'mdi-home' }}</v-icon>
                              {{ getSenderAddress(msg) }}
                            </v-chip>
                          </div>
                        </div>

                        <div class="message-content text-body-1">{{ msg.content }}</div>

                        <!-- Badges de reacciones agrupados -->
                        <div class="reaction-badges" v-if="getReactionGroups(msg).length > 0">
                          <button
                            v-for="group in getReactionGroups(msg)"
                            :key="group.type"
                            class="reaction-badge"
                            :class="{ 'reaction-badge--mine': group.isMine }"
                            @click.stop="toggleReaction(msg, group.type)"
                            :title="group.isMine ? 'Quitar reacción' : 'Reaccionar con ' + group.emoji"
                          >
                            <span class="reaction-badge-emoji">{{ group.emoji }}</span>
                            <span class="reaction-badge-count">{{ group.count }}</span>
                          </button>
                        </div>

                        <div class="message-meta d-flex justify-end align-center mt-1">
                          <span class="caption grey--text mr-1">{{ formatTime(msg.createdAt) }}</span>
                          <div class="message-actions d-flex align-center">
                            <v-menu top :close-on-content-click="true">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  small
                                  color="grey"
                                  class="action-icon mx-1"
                                  v-bind="attrs"
                                  v-on="on"
                                >mdi-emoticon-outline</v-icon>
                              </template>
                              <v-card class="pa-1 d-flex">
                                <button
                                  v-for="em in reactionEmojis"
                                  :key="em.type"
                                  class="reaction-picker-btn"
                                  :class="{ 'reaction-picker-btn--active': getMyReaction(msg) && getMyReaction(msg).reactionType === em.type }"
                                  @click.stop="toggleReaction(msg, em.type)"
                                  :title="em.label"
                                >{{ em.emoji }}</button>
                              </v-card>
                            </v-menu>
                            <v-tooltip bottom v-if="isMyMessage(msg)">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  small
                                  color="grey"
                                  class="action-icon ml-1"
                                  v-bind="attrs"
                                  v-on="on"
                                  @click="deleteMessage(msg.id)"
                                >mdi-delete-outline</v-icon>
                              </template>
                              <span>Eliminar mi mensaje</span>
                            </v-tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <div class="chat-input-bar">
              <v-text-field
                v-model="newMessage"
                :placeholder="selectedChannel.isClosed ? 'Solo administradores pueden publicar' : 'Escribe un mensaje aquí...'"
                outlined
                dense
                hide-details
                rounded
                class="mx-2 bg-white message-input-field"
                @keyup.enter="sendMessage"
                :loading="sendingMessage"
                :disabled="sendingMessage || selectedChannel.isClosed"
              ></v-text-field>
              <v-btn
                fab
                small
                depressed
                color="primary"
                class="mr-2 send-btn"
                @click="sendMessage"
                :disabled="!newMessage.trim() || sendingMessage || selectedChannel.isClosed"
              >
                <v-icon>mdi-send</v-icon>
              </v-btn>
            </div>
          </template>

          <div
            v-else-if="selectedChannel.membershipStatus === 'rejected'"
            class="channel-status-panel d-flex flex-column align-center justify-center text-center pa-5"
          >
            <v-icon size="60" color="error" class="mb-3">mdi-close-circle-outline</v-icon>
            <h3 class="grey--text">Rechazaste la invitación a este chat</h3>
          </div>
        </template>
        <template v-else>
          <div class="empty-chat-state d-flex flex-column align-center justify-center">
            <v-icon size="120" color="grey lighten-2">mdi-forum-outline</v-icon>
            <h2 class="grey--text text--darken-1 mt-4 font-weight-regular empty-chat-title">Selecciona un chat para comenzar</h2>
            <p class="grey--text text-center px-4">Comunícate con la administración y otros residentes.</p>
          </div>
        </template>
      </section>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top right>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'
import store from '@/store' // Assuming vuex store is available for auth
import { mapGetters } from 'vuex'

export default {
  name: 'CommunicationChannelResident',
  data () {
    return {
      channels: [],
      loadingChannels: false,
      searchChannel: '',
      selectedChannel: null,
      messages: [],
      messagePollingTimer: null,
      pollingMessages: false,
      loadingMessages: false,
      loadingMoreMessages: false,
      currentPage: 1,
      pageLimit: 2,
      hasMoreMessages: false,
      messagesCount: 0,
      newMessage: '',
      sendingMessage: false,
      actionLoading: false, // For accept/reject
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      snackbar: {
        show: false,
        text: '',
        color: 'success'
      },
      // Colors for avatars
      avatarColors: ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ff9800', '#ff5722', '#795548', '#607d8b'],
      // Reacciones disponibles
      reactionEmojis: [
        { type: 'like', emoji: '👍', label: 'Me gusta' },
        { type: 'dislike', emoji: '👎', label: 'No me gusta' }
      ]
    }
  },
  computed: {
    filteredChannels () {
      let filtered = this.channels
      if (this.searchChannel) {
        const term = this.searchChannel.toLowerCase()
        filtered = filtered.filter(c => c.name.toLowerCase().includes(term) || (c.description && c.description.toLowerCase().includes(term)))
      }
      // Usually residents don't want to see rejected channels in the main list, but we keep them if needed.
      return filtered.filter(c => c.membershipStatus !== 'rejected')
    },
    ...mapGetters(['getProfile']),
    currentUser () {
      // Assuming store has user info
      return this.getProfile || { id: 2, name: 'Residente Demo' } // Fallback
    },
    isMobile () {
      return this.windowWidth < 960
    },
    groupedMessages () {
      if (!this.messages.length) return []
      const groups = []
      let currentGroup = null
      this.messages.forEach(msg => {
        const label = this.getDateGroupLabel(msg.createdAt)
        const key = this.getDateGroupKey(msg.createdAt)
        if (!currentGroup || currentGroup.key !== key) {
          currentGroup = { key, label, messages: [] }
          groups.push(currentGroup)
        }
        currentGroup.messages.push(msg)
      })
      return groups
    }
  },
  mounted () {
    this.fetchChannels()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    this.stopMessagePolling()
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize () {
      this.windowWidth = window.innerWidth
    },
    closeChannelMobile () {
      this.stopMessagePolling()
      this.selectedChannel = null
      this.resetPagination()
    },
    resetPagination () {
      this.messages = []
      this.currentPage = 1
      this.hasMoreMessages = false
      this.messagesCount = 0
      this.loadingMessages = false
      this.loadingMoreMessages = false
    },
    getApiHeaders () {
      const token = localStorage.getItem('token') || store.getters.getToken
      return {
        'Authorization': `token ${token}`,
        'Content-Type': 'application/json'
      }
    },
    showNotification (text, color = 'success') {
      this.snackbar.text = text
      this.snackbar.color = color
      this.snackbar.show = true
    },
    getChannelColor (name) {
      if (!name) return this.avatarColors[0]
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
      }
      const index = Math.abs(hash) % this.avatarColors.length
      return this.avatarColors[index]
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString()
    },
    formatTime (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    startOfDay (date) {
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      return d
    },
    getDateGroupKey (dateStr) {
      if (!dateStr) return 'unknown'
      const msgDate = this.startOfDay(dateStr)
      const today = this.startOfDay(new Date())
      const diffDays = Math.round((today.getTime() - msgDate.getTime()) / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'hoy'
      if (diffDays === 1) return 'ayer'
      if (diffDays >= 2 && diffDays < 7) return 'esta-semana'
      if (diffDays >= 7 && diffDays < 14) return 'semana-pasada'
      if (diffDays >= 14 && diffDays < 30) return 'este-mes'
      return `mes-${msgDate.getFullYear()}-${msgDate.getMonth()}`
    },
    getDateGroupLabel (dateStr) {
      if (!dateStr) return 'Sin fecha'
      const key = this.getDateGroupKey(dateStr)
      const labels = {
        hoy: 'Hoy',
        ayer: 'Ayer',
        'esta-semana': 'Esta semana',
        'semana-pasada': 'La semana pasada',
        'este-mes': 'Este mes'
      }
      if (labels[key]) return labels[key]

      const msgDate = this.startOfDay(dateStr)
      const label = msgDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    async fetchChannels () {
      this.loadingChannels = true
      try {
        const res = await axios.get('https://residencial-services.intrare.app/channels/', { headers: this.getApiHeaders() })
        this.channels = res.data
      } catch (err) {
        console.error('Error fetching channels', err)
        // Fallback mock data for UI demo
        this.channels = [
          { id: '1', name: 'Anuncios Generales', description: 'Chat cerrado para avisos.', isClosed: true, membershipId: 'm1', membershipStatus: 'accepted', updatedAt: new Date().toISOString() },
          { id: '2', name: 'Vecinos Torre A', description: 'Chat para vecinos de la Torre A.', isClosed: false, membershipId: 'm2', membershipStatus: 'pending', updatedAt: new Date().toISOString() }
        ]
      } finally {
        this.loadingChannels = false
      }
    },
    selectChannel (channel) {
      this.stopMessagePolling()
      this.selectedChannel = channel
      this.resetPagination()
      if (channel.membershipStatus === 'accepted') {
        this.fetchMessages(channel.id, true)
        this.startMessagePolling(channel.id)
      }
    },
    startMessagePolling (channelId) {
      if (
        !this.selectedChannel ||
        String(this.selectedChannel.id) !== String(channelId) ||
        this.selectedChannel.membershipStatus !== 'accepted'
      ) return

      this.stopMessagePolling()
      this.messagePollingTimer = setInterval(() => {
        this.fetchLatestMessages(channelId)
      }, 5000)
    },
    stopMessagePolling () {
      if (this.messagePollingTimer !== null) {
        clearInterval(this.messagePollingTimer)
        this.messagePollingTimer = null
      }
    },
    async acceptInvitation (channel) {
      if (!channel || !channel.membershipId) return
      this.actionLoading = true
      try {
        await axios.post(`https://residencial-services.intrare.app/channel-memberships/${channel.membershipId}/accept/`, {}, { headers: this.getApiHeaders() })
        channel.membershipStatus = 'accepted'
        this.showNotification('Invitación aceptada')
        this.resetPagination()
        this.fetchMessages(channel.id, true)
        this.startMessagePolling(channel.id)
      } catch (err) {
        console.error('Error accepting invitation', err)
        channel.membershipStatus = 'accepted'
        this.showNotification('Invitación aceptada (Mock)')
        this.resetPagination()
        this.fetchMessages(channel.id, true)
        this.startMessagePolling(channel.id)
      } finally {
        this.actionLoading = false
      }
    },
    async rejectInvitation (channel) {
      if (!channel || !channel.membershipId) return
      this.actionLoading = true
      try {
        await axios.post(`https://residencial-services.intrare.app/channel-memberships/${channel.membershipId}/reject/`, {}, { headers: this.getApiHeaders() })
        channel.membershipStatus = 'rejected'
        this.stopMessagePolling()
        this.selectedChannel = null
        this.resetPagination()
        this.showNotification('Invitación rechazada', 'info')
      } catch (err) {
        console.error('Error rejecting invitation', err)
        channel.membershipStatus = 'rejected'
        this.stopMessagePolling()
        this.selectedChannel = null
        this.resetPagination()
        this.showNotification('Invitación rechazada (Mock)', 'info')
      } finally {
        this.actionLoading = false
      }
    },
    async fetchMessages (channelId, reset = false) {
      if (reset) {
        this.loadingMessages = true
        this.currentPage = 1
        this.messages = []
        this.hasMoreMessages = false
      } else {
        if (this.loadingMoreMessages || !this.hasMoreMessages) return
        this.loadingMoreMessages = true
      }

      try {
        const page = reset ? 1 : this.currentPage + 1
        const res = await axios.get(
          `https://residencial-services.intrare.app/channel-messages/paginated/?idChannel=${channelId}&page=${page}&limit=${this.pageLimit}`,
          { headers: this.getApiHeaders() }
        )
        const payload = res.data || {}
        const results = Array.isArray(payload.results) ? payload.results : []
        // API returns newest → oldest; reverse for chronological chat display
        const chronological = results.slice().reverse()
        const existingIds = new Set(this.messages.map(m => m.id))
        const uniqueNew = chronological.filter(m => !existingIds.has(m.id))

        if (reset) {
          this.messages = uniqueNew
          this.currentPage = 1
          this.messagesCount = payload.count || this.messages.length
          this.hasMoreMessages = !!payload.next
          this.scrollToBottom()
          this.$nextTick(() => {
            this.fillViewportIfNeeded(channelId)
          })
        } else {
          const container = this.$refs.messagesContainer
          const prevScrollHeight = container ? container.scrollHeight : 0
          this.messages = [...uniqueNew, ...this.messages]
          this.currentPage = page
          this.messagesCount = payload.count || this.messages.length
          this.hasMoreMessages = !!payload.next
          this.$nextTick(() => {
            if (container) {
              container.scrollTop = container.scrollHeight - prevScrollHeight
            }
            this.fillViewportIfNeeded(channelId)
          })
        }
      } catch (err) {
        console.error('Error fetching messages', err)
        if (reset) {
          this.messages = []
          this.hasMoreMessages = false
        }
        this.showNotification('No se pudieron cargar los mensajes', 'error')
      } finally {
        this.loadingMessages = false
        this.loadingMoreMessages = false
      }
    },
    async fetchLatestMessages (channelId) {
      if (
        this.pollingMessages ||
        this.loadingMessages ||
        !this.selectedChannel ||
        String(this.selectedChannel.id) !== String(channelId) ||
        this.selectedChannel.membershipStatus !== 'accepted'
      ) return

      this.pollingMessages = true
      try {
        const res = await axios.get(
          `https://residencial-services.intrare.app/channel-messages/paginated/?idChannel=${channelId}&page=1&limit=20`,
          { headers: this.getApiHeaders() }
        )
        if (
          !this.selectedChannel ||
          String(this.selectedChannel.id) !== String(channelId) ||
          this.selectedChannel.membershipStatus !== 'accepted'
        ) return

        const payload = res.data || {}
        const results = Array.isArray(payload.results) ? payload.results : []
        const latestMessages = results.slice().reverse()
        const latestById = new Map(latestMessages.map(message => [String(message.id), message]))
        const existingIds = new Set(this.messages.map(message => String(message.id)))
        const hasNewMessages = latestMessages.some(message => !existingIds.has(String(message.id)))
        const container = this.$refs.messagesContainer
        const wasAtBottom = container &&
          container.scrollHeight - container.scrollTop - container.clientHeight <= 80

        const mergedMessages = this.messages.map(message => latestById.get(String(message.id)) || message)
        latestMessages.forEach(message => {
          if (!existingIds.has(String(message.id))) mergedMessages.push(message)
        })
        mergedMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        this.messages = mergedMessages

        if (payload.count !== undefined && payload.count !== null) {
          this.messagesCount = payload.count
        }
        this.hasMoreMessages = !!payload.next
        if (hasNewMessages && wasAtBottom) this.scrollToBottom()
      } catch (err) {
        console.error('Error polling messages', err)
      } finally {
        this.pollingMessages = false
      }
    },
    fillViewportIfNeeded (channelId) {
      const container = this.$refs.messagesContainer
      if (
        container &&
        this.hasMoreMessages &&
        !this.loadingMoreMessages &&
        !this.loadingMessages &&
        container.scrollHeight <= container.clientHeight + 20
      ) {
        this.fetchMessages(channelId, false)
      }
    },
    onMessagesScroll (event) {
      const container = event.target
      if (!container || !this.selectedChannel) return
      if (
        container.scrollTop <= 80 &&
        this.hasMoreMessages &&
        !this.loadingMoreMessages &&
        !this.loadingMessages
      ) {
        this.fetchMessages(this.selectedChannel.id, false)
      }
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    isMyMessage (msg) {
      // Use senderData.id (UUID string) if present; fallback to sender int id
      if (msg.senderData && msg.senderData.id) {
        return String(msg.senderData.id) === String(this.currentUser.id)
      }
      return msg.sender === this.currentUser.id || msg.senderName === 'Tú'
    },
    isAdminMessage (msg) {
      // The backend sets address = 'Administración' for admin senders
      return msg.senderData && msg.senderData.address === 'Administración'
    },
    getSenderFullName (msg) {
      if (msg.senderData) {
        return `${msg.senderData.first_name || ''} ${msg.senderData.last_name || ''}`.trim() || 'Sin nombre'
      }
      return msg.senderName || 'Desconocido'
    },
    getSenderInitial (msg) {
      const name = this.getSenderFullName(msg)
      return name.charAt(0).toUpperCase()
    },
    getSenderAddress (msg) {
      return (msg.senderData && msg.senderData.address) ? msg.senderData.address : 'Sin dirección'
    },
    getMessageClass (msg) {
      if (this.isMyMessage(msg)) return 'message-mine'
      if (this.isAdminMessage(msg)) return 'message-admin'
      return 'message-resident'
    },
    async sendMessage () {
      if (!this.newMessage.trim() || !this.selectedChannel || this.selectedChannel.isClosed) return
      this.sendingMessage = true
      try {
        const payload = {
          idChannel: this.selectedChannel.id,
          content: this.newMessage
        }
        const res = await axios.post('https://residencial-services.intrare.app/channel-messages/', payload, { headers: this.getApiHeaders() })
        this.messages.push(res.data)
        this.messagesCount += 1
        this.newMessage = ''
        this.scrollToBottom()
      } catch (err) {
        console.error('Error sending message', err)
        this.messages.push({
          id: Date.now().toString(),
          idChannel: this.selectedChannel.id,
          content: this.newMessage,
          sender: this.currentUser.id,
          senderData: {
            id: String(this.currentUser.id),
            first_name: this.currentUser.first_name || 'Yo',
            last_name: this.currentUser.last_name || '',
            email: this.currentUser.email || '',
            avatar: null,
            address: this.currentUser.address || 'Mi Casa'
          },
          reactions: [],
          createdAt: new Date().toISOString()
        })
        this.messagesCount += 1
        this.newMessage = ''
        this.scrollToBottom()
      } finally {
        this.sendingMessage = false
      }
    },
    async deleteMessage (messageId) {
      if (!confirm('¿Estás seguro de que deseas eliminar tu mensaje?')) return
      try {
        await axios.delete(`https://residencial-services.intrare.app/channel-messages/${messageId}/`, { headers: this.getApiHeaders() })
        this.messages = this.messages.filter(m => m.id !== messageId)
        this.messagesCount = Math.max(0, this.messagesCount - 1)
        this.showNotification('Mensaje eliminado')
      } catch (err) {
        console.error('Error deleting message', err)
        this.messages = this.messages.filter(m => m.id !== messageId)
        this.messagesCount = Math.max(0, this.messagesCount - 1)
        this.showNotification('Mensaje eliminado (Mock)')
      }
    },
    // --- Reactions ---
    getMyReaction (msg) {
      if (!msg.reactions || !msg.reactions.length) return null
      const userId = String(this.currentUser.id)
      return msg.reactions.find(r => String(r.idUser) === userId) || null
    },
    getReactionGroups (msg) {
      if (!msg.reactions || !msg.reactions.length) return []
      const userId = String(this.currentUser.id)
      const counts = {}
      msg.reactions.forEach(r => {
        if (!counts[r.reactionType]) {
          counts[r.reactionType] = { type: r.reactionType, count: 0, isMine: false }
        }
        counts[r.reactionType].count++
        if (String(r.idUser) === userId) {
          counts[r.reactionType].isMine = true
        }
      })
      return Object.values(counts).map(g => {
        const found = this.reactionEmojis.find(e => e.type === g.type)
        return {
          ...g,
          emoji: found ? found.emoji : '❓'
        }
      })
    },
    async toggleReaction (msg, type) {
      const existing = this.getMyReaction(msg)
      if (existing) {
        if (existing.reactionType === type) {
          // Misma reacción → eliminar
          await this.deleteReaction(msg, existing)
        } else {
          // Distinta reacción → cambiar (upsert)
          await this.upsertReaction(msg, type, existing)
        }
      } else {
        // Sin reacción propia → crear
        await this.upsertReaction(msg, type, null)
      }
    },
    async upsertReaction (msg, type, existing) {
      const userId = String(this.currentUser.id)
      // Actualización optimista
      if (!msg.reactions) this.$set(msg, 'reactions', [])
      if (existing) {
        const idx = msg.reactions.indexOf(existing)
        if (idx !== -1) {
          this.$set(msg.reactions, idx, { ...existing, reactionType: type })
        }
      } else {
        msg.reactions.push({ reactionType: type, idUser: userId, id: null })
      }
      try {
        const payload = { idMessage: msg.id, reactionType: type }
        const res = await axios.post(
          'https://residencial-services.intrare.app/message-reactions/',
          payload,
          { headers: this.getApiHeaders() }
        )
        // Sincronizar el id real devuelto por el API
        const saved = res.data
        if (saved && saved.id) {
          const target = msg.reactions.find(
            r => String(r.idUser) === userId && r.reactionType === type
          )
          if (target) this.$set(target, 'id', saved.id)
        }
      } catch (err) {
        console.error('Error al reaccionar', err)
      }
    },
    async deleteReaction (msg, reaction) {
      // Eliminación optimista
      const idx = msg.reactions.indexOf(reaction)
      if (idx !== -1) msg.reactions.splice(idx, 1)
      if (!reaction.id) return // Sin id real todavía, nada que borrar en servidor
      try {
        await axios.delete(
          `https://residencial-services.intrare.app/message-reactions/${reaction.id}/`,
          { headers: this.getApiHeaders() }
        )
      } catch (err) {
        console.error('Error al eliminar reacción', err)
        // Revertir si falla
        msg.reactions.push(reaction)
      }
    }
  }
}
</script>

<style scoped>
.whatsapp-container {
  height: calc(100vh - 88px); /* app-bar 64px + padding del contenedor */
  max-height: calc(100vh - 88px);
  width: 100%;
  max-width: none;
  overflow: hidden;
  background-color: #f0f2f5;
  box-sizing: border-box;
}

.whatsapp-panels {
  display: flex;
  flex-direction: row;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.sidebar {
  display: flex;
  flex-direction: column;
  width: 34%;
  max-width: 420px;
  min-width: 280px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background-color: #ffffff;
  border-right: 1px solid #d1d7db;
  flex-shrink: 0;
}

.sidebar-top {
  flex-shrink: 0;
  background-color: #ffffff;
}

.sidebar-header,
.chat-header {
  flex-shrink: 0;
  border-bottom: 1px solid #e9edef;
}

.sidebar-title {
  font-size: 1rem !important;
}

.sidebar-search {
  background-color: #ffffff;
}

.search-input {
  border-radius: 8px;
}
.search-input .v-input__slot {
  background-color: #f0f2f5 !important;
}

.channel-list-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.channel-list {
  background: transparent;
}

.channel-item {
  border-bottom: 1px solid #f2f2f2;
  cursor: pointer;
}
.channel-item:hover {
  background-color: #f5f6f6;
}
.active-channel {
  background-color: #f0f2f5;
}

.channel-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 70%;
}

.chat-area {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background-color: #efeae2;
  background-image:
    linear-gradient(rgba(229, 221, 213, 0.85), rgba(229, 221, 213, 0.85));
}

.chat-top {
  flex-shrink: 0;
  z-index: 2;
}

.chat-header-title {
  min-width: 0;
  overflow: hidden;
  line-height: 1.2;
}

.chat-header-desc {
  max-width: min(400px, 40vw);
}

.chat-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 5% 20px;
  -webkit-overflow-scrolling: touch;
}

.chat-input-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  background-color: #f0f2f5;
  border-top: 1px solid #d1d7db;
  z-index: 2;
}

.send-btn {
  flex-shrink: 0;
}

.channel-status-panel {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  background-color: rgba(255, 255, 255, 0.75);
}

.status-title {
  text-align: center;
  padding: 0 12px;
}

.message-date-group {
  width: 100%;
}

.date-separator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 12px 0 16px;
  position: sticky;
  top: 4px;
  z-index: 5;
}

.date-separator-label {
  background-color: rgba(255, 255, 255, 0.95);
  color: #54656f;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 14px;
  border-radius: 8px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.08);
  letter-spacing: 0.2px;
}

.message-wrapper {
  width: 100%;
}

.message-card {
  max-width: 65%;
  border-radius: 8px;
  position: relative;
  word-wrap: break-word;
}

.sender-info {
  min-width: 0;
}

.sender-address-chip {
  height: 16px !important;
  font-size: 9px !important;
  max-width: 160px;
}

.message-mine {
  background-color: #d9fdd3 !important;
  border-top-right-radius: 0;
}

/* Mensajes del Administrador: fondo lila suave con borde violeta */
.message-admin {
  background-color: #ede7f6 !important;
  border-top-left-radius: 0;
  border-left: 3px solid #7e57c2;
}

/* Mensajes de otros Residentes: blanco con borde teal */
.message-resident {
  background-color: #ffffff !important;
  border-top-left-radius: 0;
  border-left: 3px solid #26a69a;
}

.action-icon {
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
}

.message-card:hover .action-icon,
.message-card:focus-within .action-icon {
  opacity: 1;
}

.empty-chat-state {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  background-color: #f0f2f5;
  border-bottom: 6px solid #25d366;
  overflow: hidden;
}

.empty-chat-title {
  text-align: center;
  padding: 0 16px;
}

.bg-white-transparent {
  background-color: rgba(255, 255, 255, 0.9);
}

.channel-list-scroll::-webkit-scrollbar,
.chat-messages::-webkit-scrollbar {
  width: 6px;
}
.channel-list-scroll::-webkit-scrollbar-track,
.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}
.channel-list-scroll::-webkit-scrollbar-thumb,
.chat-messages::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}
.channel-list-scroll::-webkit-scrollbar-thumb:hover,
.chat-messages::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

@media (max-width: 1263px) {
  .sidebar {
    width: 38%;
    max-width: 360px;
    min-width: 260px;
  }

  .message-card {
    max-width: 75%;
  }

  .chat-messages {
    padding: 14px 4% 16px;
  }
}

@media (max-width: 959px) {
  .whatsapp-container {
    height: calc(100vh - 72px); /* app-bar móvil ~56px + padding */
    max-height: calc(100vh - 72px);
  }

  .whatsapp-panels {
    flex-direction: column;
  }

  .sidebar,
  .chat-area {
    width: 100%;
    max-width: none;
    min-width: 0;
    height: 100%;
  }

  .chat-messages {
    padding: 12px 10px 16px;
  }

  .message-card {
    max-width: 88%;
  }

  .chat-header-desc {
    max-width: 180px;
  }

  .sender-address-chip {
    max-width: 120px;
  }

  .action-icon {
    opacity: 0.7;
  }

  .chat-input-bar {
    padding: 8px 6px;
  }

  .message-input-field {
    font-size: 14px;
  }
}

@media (max-width: 600px) {
  .whatsapp-container {
    height: calc(100vh - 72px);
    max-height: calc(100vh - 72px);
  }

  .message-card {
    max-width: 92%;
  }

  .chat-header-desc {
    max-width: 120px;
  }

  .date-separator-label {
    font-size: 11px;
    padding: 3px 10px;
  }

  .empty-chat-state .v-icon {
    font-size: 80px !important;
  }

  .sidebar {
    min-width: 0;
  }
}

/* ========== Reaction System ========== */

/* Contenedor relativo del mensaje + picker flotante */
.message-outer {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  max-width: 65%;
}

.message-outer--mine {
  align-items: flex-end;
}

.message-outer--other {
  align-items: flex-start;
}

/* El message-card dentro del outer ya no necesita max-width propio */
.message-outer .message-card {
  max-width: 100%;
  width: 100%;
}

.reaction-picker-btn {
  font-size: 20px;
  line-height: 1;
  padding: 3px 4px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.15s, background-color 0.15s;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.reaction-picker-btn:hover {
  transform: scale(1.3);
  background-color: #f0f2f5;
}

.reaction-picker-btn--active {
  background-color: #e3f2fd;
  transform: scale(1.15);
}

/* Badges de reacciones bajo el contenido */
.reaction-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 5px;
}

.reaction-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: rgba(255, 255, 255, 0.85);
  border: 1.5px solid #e0e0e0;
  border-radius: 12px;
  padding: 2px 7px 2px 5px;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, transform 0.1s;
  outline: none;
  line-height: 1.3;
  user-select: none;
}

.reaction-badge:hover {
  background-color: #f0f0f0;
  transform: scale(1.06);
}

.reaction-badge:active {
  transform: scale(0.97);
}

/* Reacción propia: resaltada con color primario */
.reaction-badge--mine {
  background-color: #e3f2fd;
  border-color: #1976d2;
}

.reaction-badge-emoji {
  font-size: 15px;
  line-height: 1;
}

.reaction-badge-count {
  font-size: 11px;
  font-weight: 600;
  color: #54656f;
}

.reaction-badge--mine .reaction-badge-count {
  color: #1565c0;
}

/* Responsive: ajustar max-width del outer igual que message-card */
@media (max-width: 1263px) {
  .message-outer {
    max-width: 75%;
  }
}

@media (max-width: 959px) {
  .message-outer {
    max-width: 88%;
  }

  /* Reemplazar por tap en el badge para toggle — picker no flotante en mobile */
  .reaction-badges {
    margin-top: 4px;
  }

  .reaction-badge {
    padding: 3px 8px 3px 6px;
  }

  .reaction-picker-btn {
    font-size: 18px;
    padding: 2px 3px;
  }
}

@media (max-width: 600px) {
  .message-outer {
    max-width: 92%;
  }

  .reaction-badge {
    font-size: 12px;
    padding: 2px 6px 2px 4px;
  }

  .reaction-badge-emoji {
    font-size: 14px;
  }
}
</style>
